<script>
    import { scale } from "svelte/transition";
</script>

<main
    class="parts"
    in:scale={{ start: 0.8, duration: 1000 }}
    out:scale={{ start: 1.2, duration: 1000 }}
>
    <div class="ctn">
        <div class="ctn-text">
            <h3>Installation de React Native</h3>
            <p>
                Comme l'indique la <a
                    href="https://reactnative.dev/docs/environment-setup"
                >
                    documentation officielle de React Native,
                </a>
                pour installer React Native, cela va dépendre de l'OS que l'on vise
                (Android/IOS) et de l'OS de développement (macOS/Windows/Linux).
                Pour ma part, étant sur Windows et utilisant Android Studio et mon
                propre téléphone sous Android pour tester l'application, j'utilise
                Expo CLI. Après avoir setup toute la partie Android Studio, je fais
                <code>npx react-native init <i>nom-du-projet</i></code> pour
                créer le projet. Si tout se passe sans encombres, pour
                initialiser le projet, il faut faire
                <code>npx react-native start</code> ou <code>npm start</code> et
                ensuite
                <code>npx react-native run-android</code>
                ou <code>npm run android</code> pour lancer le projet. Le premier
                lancement peut prendre du temps lors des différents téléchargements
                et peut ne pas réussir à lancer l'application au premier essaie.
            </p>
            <h3>Architecture des fichiers</h3>
            <p>Les dossiers importants sont :</p>
            <ul>
                <li>
                    <code>node_modules</code> : c’est là que sont installées toutes
                    les dépendances de notre code. Ce dossier peut vite devenir très
                    volumineux. Les utilisateurs de framework comme Angular le connaisse
                    très bien.
                </li>
                <li>
                    <code>public</code> : dans ce dossier, vous trouverez votre fichier
                    index.html et d’autres fichiers relatifs au référencement web
                    de votre page.
                </li>
                <li>
                    <code>src</code> : c'est le dossier majoritairement modifié.
                    L’essentiel des fichiers que vous créerez et modifierez seront
                    là. Celui-ci peut ne pas être présent lors de la création du
                    projet.
                </li>
            </ul>
            <p>Les fichiers importants sont :</p>
            <ul>
                <li>
                    <code>package.json</code>
                    : situé à la racine de votre projet, il vous permet de gérer
                    vos dépendances (tous les outils permettant de construire votre
                    projet), vos scripts qui peuvent être exécutés avec npm ou yarn,
                    etc.
                </li>
                <li>
                    dans /src, il y a <code>index.js</code> qui permet d’initialiser
                    notre app React
                </li>
                <li>
                    et enfin, dans /src, vous trouvez <code>App.js</code> qui est
                    notre premier composant React.
                </li>
            </ul>
        </div>
        <div class="ctn-img">
            <img
                id="launch-app"
                src="img/launch-app.gif"
                alt="démarrage application"
            />
        </div>
    </div>
    <h3>Fonctionnement du code</h3>
    <p>
        L'organisation des fichiers dans src se fait de la manière que l'on
        veut. Généralement on crée un dossier components dans src pour y mettre
        les composants.
    </p>
    <p>
        La constitution classique d'un fichier avec une classe est de la sorte :
    </p>
    <div class="code">
        <p>
            <b class="pink">import</b> <b class="light-blue">React</b>
            <b class="pink">from</b> <b class="orange">"react"</b>;<br />
            <b class="pink">import</b> &lbrace
            <b class="light-blue">StyleSheet</b>,
            <b class="light-blue">View</b>
            &rbrace <b class="pink">from</b>
            <b class="orange">"react-native"</b>;
            <br /><br />
            <b class="pink">export</b> <b class="blue">class</b>
            <b class="green"><i>nomDuComposant</i></b>
            <b class="blue">extends</b> <b class="green">React.Component</b>
            &lbrace<br />
            &nbsp; <b class="blue">constructor</b>(<b class="light-blue"
                >props</b
            >) &lbrace<br />
            &nbsp;&nbsp; <b class="blue">super</b>(<b class="light-blue"
                >props</b
            >);<br />
            &nbsp; &rbrace<br />
            &nbsp; <b class="yellow">render</b>() &lbrace<br />
            &nbsp;&nbsp; <b class="pink">return</b> (<br />
            &nbsp;&nbsp;&nbsp; <b class="grey">&lt;</b><b class="green">View</b>
            <b class="light-blue">style</b>=<b class="mid-blue"
                >&lbrace;styles</b
            >.<b class="light-blue">ctnStyle</b><b class="mid-blue">&rbrace;</b>
            <b class="grey">/&gt;</b> <br />
            &nbsp;&nbsp; ) <br />
            &rbrace;&rbrace <br />
            <br />
            <b class="blue">const</b> <b class="mid-blue">styles</b> =
            <b class="green">StyleSheet</b>.<b class="yellow">create</b
            >(&lbrace <br />
            &nbsp; <b class="light-blue">ctnStyle:</b> &lbrace <br />
            &nbsp;&nbsp; <b class="light-blue">width:</b>
            <b class="light-green">10</b>, <br />
            &nbsp;&nbsp; <b class="light-blue">height:</b>
            <b class="light-green">10</b>, <br />
            &nbsp;&nbsp; <b class="light-blue">borderRadius:</b>
            <b class="light-green">5</b>,<br />
            &nbsp;&nbsp; <b class="light-blue">backgroundColor:</b>
            <b class="orange">"red"</b>, <br />
            &nbsp; &rbrace; <br />
            &rbrace;)
        </p>
    </div>
    <ul>
        <li>
            Les <code class="pink">import</code> sont situés en haut du fichier.
            Si on veut importer des librairies, il faut les installer avec en
            faisant
            <code>npm install <i>nom-de-la-librairie</i></code> et on pourra faire
            l'import ensuite.
        </li>
        <li>
            Les <code class="light-blue">props</code> du
            <code class="blue">constructor</code>
            sont en quelques sorte les paramètres possible à envoyer à l'appel du
            composant. Par exemple, en appelant un composant
            <code
                ><b class="grey">&lt</b><b class="green">Switch</b>
                <b class="light-blue">active</b>=<b class="blue">&lbrace;</b><b
                    class="blue">true</b
                ><b class="blue">&rbrace;</b><b class="grey">&gt</b><b
                    class="grey">&lt/</b
                ><b class="green">Switch</b><b class="grey">&gt</b>
            </code>
            on appel la props <code class="light-blue">active</code> à la valeur
            <code class="blue">true</code> On récupère cette valeur avec
            <code
                ><b class="light-blue">props</b>.<b class="light-blue">active</b
                ></code
            >
        </li>
        <li>
            La fonction <code><b class="yellow">render</b>()</code> est la
            fonction lancée par la classe pour afficher les éléments d'un
            composant. Les éléments en React Native ne sont pas des éléments
            HTML, mais des éléments
            <a href="https://fr.reactjs.org/docs/introducing-jsx.html">JSX</a>
            qui est un mélance d'XML et d'HTML. Ici, on y retourne une
            <code
                ><b class="grey">&lt</b><b class="green">View</b><b class="grey"
                    >&gt</b
                ></code
            >
            qui est l'équivalent d'une
            <code
                ><b class="grey">&lt</b><b class="blue">div</b><b class="grey"
                    >&gt</b
                ></code
            >
            en HTML. La props présente sur la View est
            <code class="light-blue">style</code> qui, comme son nom l'indique,
            permet de rajouter des styles à la view. Ici on y met le style nommé
            <code class="light-blue">ctnStyle</code>
        </li>
        <li>
            Pour les styles, il est possible d'injecter directement du style
            dans la props <code class="light-blue">style</code> en mettant 2
            accolades au lieu d'une :
            <code
                ><b class="light-blue">style</b>=<b class="mid-blue">&lbrace</b
                >&lbrace <b class="light-blue">backgroundColor:</b>
                <b class="orange">'blue'</b>
                &rbrace<b class="mid-blue">&rbrace</b></code
            >
        </li>
        <li>
            La manière la plus organisée de regrouper les styles est de créer
            une variable <code class="mid-blue">styles</code> en dehors de la
            class pour pouvoir y acceder partout. Pour cela on importe
            <code class="green">StyleSheet</code> et on utilise la fonction
            <code><b class="yellow">create</b>()</code> pour créer une feuille de
            style. Proche du CSS, les styles sont nommés de la même façon mais en
            utilisant le Camel case. A la différence du CSS, tout les objets créés
            sont des classes utilisables plusieurs fois.
        </li>
    </ul>
</main>

<style>
    .ctn {
        display: flex;
        flex-direction: row;
    }

    .ctn-text {
        width: 70%;
    }

    .ctn-img {
        width: 30%;
        margin: 1rem 0 1rem 5%;
    }
    
    #launch-app {
        display: block;
        width: 100%;
        height: auto;
        box-shadow: 0 0 10px 5px #00000055;
    }
</style>
